// ── ScheduleOverview — 일/주/월 전체 직원 스케줄 조감 ──────
// 일: 직원 행 × 24h, work 블록만 (break는 work 사이 빈 공간으로 인식)
// 주: 7일 × 직원 카드 (work + break 시간 모두 표시)
// 월: 캘린더 + 각 날짜에 누가 몇시~몇시 일하는지 직접 표시

import React, { useState } from 'react';

const KOR_DAYS = ['일', '월', '화', '수', '목', '금', '토'];
const COLORS = ['#FF6F0F', '#2563eb', '#52525B', '#9333ea', '#A1A1AA', '#0f766e', '#be185d', '#7c3aed', '#0891b2', '#ea580c'];

function timeToMin(t) {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}
function pad(n) { return String(n).padStart(2, '0'); }
function colorFor(empIdx) { return COLORS[empIdx % COLORS.length]; }
function minToTimeStr(m) {
  m = ((m % (24 * 60)) + 24 * 60) % (24 * 60);
  return `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
}

function getBlocks(sh) {
  if (!sh || sh.isOff) return { works: [], breaks: [] };
  const blocks = Array.isArray(sh.blocks) && sh.blocks.length > 0
    ? sh.blocks
    : (sh.startTime && sh.endTime ? [{ type: 'work', start: sh.startTime, end: sh.endTime }] : []);
  return {
    works: blocks.filter(b => b.type === 'work').sort((a, b) => timeToMin(a.start) - timeToMin(b.start)),
    breaks: blocks.filter(b => b.type === 'break').sort((a, b) => timeToMin(a.start) - timeToMin(b.start)),
  };
}

function blockMinutes(b) {
  let s = timeToMin(b.start), e = timeToMin(b.end);
  if (e <= s) e += 24 * 60;
  return Math.max(0, e - s);
}

function shiftNetMin(sh) {
  const { works, breaks } = getBlocks(sh);
  let workMin = works.reduce((s, b) => s + blockMinutes(b), 0);
  let breakMin = breaks.reduce((s, b) => s + blockMinutes(b), 0);
  if (breakMin === 0 && sh?.breakMinutes > 0) breakMin = Number(sh.breakMinutes);
  return Math.max(0, workMin - breakMin);
}

// work 블록을 break 시간대에서 잘라내 segments 반환 (work만 남기고 break는 공백)
function buildWorkSegments(works, breaks) {
  const segments = [];
  const breakRanges = breaks.map(b => ({ s: timeToMin(b.start), e: timeToMin(b.end) }));
  for (const w of works) {
    let ws = timeToMin(w.start);
    let we = timeToMin(w.end);
    if (we <= ws) we = 24 * 60;
    const inside = breakRanges
      .map(b => ({ s: Math.max(b.s, ws), e: Math.min(b.e, we) }))
      .filter(b => b.e > b.s)
      .sort((a, b) => a.s - b.s);

    let cursor = ws;
    for (const br of inside) {
      if (br.s > cursor) segments.push({ startM: cursor, endM: br.s });
      cursor = br.e;
    }
    if (cursor < we) segments.push({ startM: cursor, endM: we });
  }
  return segments;
}

export default function ScheduleOverview({ employees = [], schedules = {} }) {
  const [view, setView] = useState('day');
  const [refDate, setRefDate] = useState(new Date());
  const activeEmps = employees.filter(e => e.active !== false);

  if (activeEmps.length === 0) {
    return (
      <div style={{ padding: '32px', textAlign: 'center', background: '#fff',
                    borderRadius: '10px', border: '1px solid #e5e7eb', color: '#9ca3af', fontSize: '13px' }}>
        직원이 등록되지 않았습니다.
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '4px', background: '#f3f4f6', padding: '3px', borderRadius: '8px' }}>
          {[['day','일'],['week','주'],['month','월']].map(([k, label]) => (
            <button key={k} onClick={() => setView(k)}
              style={{ padding: '6px 14px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                       fontSize: '13px', fontWeight: '700',
                       background: view === k ? '#fff' : 'transparent',
                       color: view === k ? '#FF6F0F' : '#6b7280',
                       boxShadow: view === k ? '0 1px 3px rgba(0,0,0,0.08)' : 'none' }}>
              {label}
            </button>
          ))}
        </div>
        <DateNav view={view} refDate={refDate} setRefDate={setRefDate} />
      </div>

      {view === 'day'   && <DayView   refDate={refDate} employees={activeEmps} schedules={schedules} />}
      {view === 'week'  && <WeekView  refDate={refDate} employees={activeEmps} schedules={schedules} />}
      {view === 'month' && <MonthView refDate={refDate} employees={activeEmps} schedules={schedules} />}
    </div>
  );
}

function DateNav({ view, refDate, setRefDate }) {
  const move = delta => {
    const d = new Date(refDate);
    if (view === 'day') d.setDate(d.getDate() + delta);
    else if (view === 'week') d.setDate(d.getDate() + delta * 7);
    else d.setMonth(d.getMonth() + delta);
    setRefDate(d);
  };

  let label = '';
  if (view === 'day') {
    label = `${refDate.getFullYear()}-${pad(refDate.getMonth() + 1)}-${pad(refDate.getDate())} (${KOR_DAYS[refDate.getDay()]})`;
  } else if (view === 'week') {
    const start = new Date(refDate);
    const dow = start.getDay();
    const offset = dow === 0 ? -6 : 1 - dow;
    start.setDate(start.getDate() + offset);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    label = `${pad(start.getMonth() + 1)}/${pad(start.getDate())} ~ ${pad(end.getMonth() + 1)}/${pad(end.getDate())}`;
  } else {
    label = `${refDate.getFullYear()}년 ${refDate.getMonth() + 1}월`;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
      <button onClick={() => move(-1)}
        style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #e5e7eb',
                 background: '#fff', cursor: 'pointer', fontSize: '13px' }}>‹</button>
      <strong style={{ fontSize: '14px', flex: 1, textAlign: 'center', minWidth: '140px' }}>{label}</strong>
      <button onClick={() => move(1)}
        style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #e5e7eb',
                 background: '#fff', cursor: 'pointer', fontSize: '13px' }}>›</button>
      <button onClick={() => setRefDate(new Date())}
        style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #e5e7eb',
                 background: '#fff', cursor: 'pointer', fontSize: '11px', color: '#6b7280' }}>오늘</button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// 일별: work 블록만 (break는 공백)
// ─────────────────────────────────────────────────────────
function DayView({ refDate, employees, schedules }) {
  const y = refDate.getFullYear(), m = refDate.getMonth() + 1, d = refDate.getDate();
  const monthKey = `${y}-${pad(m)}`;
  const dayData = schedules?.[monthKey]?.[d] || {};

  const dayShifts = employees.map(emp => ({
    emp,
    shift: dayData[emp.id] || null,
    blocks: getBlocks(dayData[emp.id]),
    netMin: shiftNetMin(dayData[emp.id]),
  }));
  const totalNetMin = dayShifts.reduce((s, ds) => s + ds.netMin, 0);
  const workingCount = dayShifts.filter(ds => ds.netMin > 0).length;

  return (
    <div>
      <SummaryBar items={[
        { label: '근무자', value: `${workingCount}/${employees.length}명` },
        { label: '총 근무', value: `${(totalNetMin / 60).toFixed(1)}h` },
      ]} />

      <div style={{ background: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb',
                    maxHeight: '60vh', overflowY: 'auto', overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch' }}>
        <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', minWidth: '1200px',
                      background: '#fafaf9', position: 'sticky', top: 0, zIndex: 10 }}>
          <div style={{ width: '90px', padding: '8px 10px', fontSize: '11px',
                        fontWeight: '700', color: '#6b7280', borderRight: '1px solid #e5e7eb' }}>
            직원
          </div>
          <div style={{ flex: 1, position: 'relative', height: '32px' }}>
            {Array.from({ length: 25 }).map((_, h) => (
              <div key={h} style={{
                position: 'absolute', left: `${(h / 24) * 100}%`,
                fontSize: h % 3 === 0 ? '10px' : '9px',
                color: h % 3 === 0 ? '#6b7280' : '#d1d5db',
                fontWeight: h % 3 === 0 ? '700' : '400',
                top: h % 3 === 0 ? '8px' : '14px',
                transform: 'translateX(-50%)',
              }}>{h}</div>
            ))}
            <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0,
                          width: `${(6 / 24) * 100}%`, background: 'rgba(99,102,241,0.06)' }} />
            <div style={{ position: 'absolute', top: 0, bottom: 0, right: 0,
                          width: `${(2 / 24) * 100}%`, background: 'rgba(99,102,241,0.06)' }} />
          </div>
        </div>

        {dayShifts.map((ds, idx) => {
          const segments = ds.netMin > 0 ? buildWorkSegments(ds.blocks.works, ds.blocks.breaks) : [];
          return (
            <div key={ds.emp.id} style={{
              display: 'flex', alignItems: 'stretch', minWidth: '1200px',
              borderBottom: idx < dayShifts.length - 1 ? '1px solid #f3f4f6' : 'none',
              minHeight: '52px',
            }}>
              <div style={{ width: '90px', padding: '8px 10px', borderRight: '1px solid #e5e7eb',
                            fontSize: '12px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ fontWeight: '700', color: colorFor(idx) }}>{ds.emp.name}</div>
                <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>
                  {ds.emp.position || '직원'} · {ds.netMin > 0 ? `${(ds.netMin / 60).toFixed(1)}h` : 'OFF'}
                </div>
              </div>
              <div style={{ flex: 1, position: 'relative', background: idx % 2 ? '#fafaf9' : '#fff' }}>
                {Array.from({ length: 23 }).map((_, i) => {
                  const h = i + 1;
                  return (
                    <div key={h} style={{
                      position: 'absolute', top: 0, bottom: 0, left: `${(h / 24) * 100}%`,
                      width: '1px',
                      background: h % 3 === 0 ? '#e5e7eb' : '#f3f4f6',
                    }} />
                  );
                })}
                <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0,
                              width: `${(6 / 24) * 100}%`, background: 'rgba(99,102,241,0.05)' }} />
                <div style={{ position: 'absolute', top: 0, bottom: 0, right: 0,
                              width: `${(2 / 24) * 100}%`, background: 'rgba(99,102,241,0.05)' }} />

                {/* work segment만 그림 — break는 공백으로 처리됨 */}
                {segments.map((seg, i) => {
                  const left = `${(seg.startM / (24 * 60)) * 100}%`;
                  const width = `${((seg.endM - seg.startM) / (24 * 60)) * 100}%`;
                  const startStr = minToTimeStr(seg.startM);
                  const endStr = seg.endM === 24 * 60 ? '24:00' : minToTimeStr(seg.endM);
                  const lenH = (seg.endM - seg.startM) / 60;
                  return (
                    <div key={i} title={`${startStr}~${endStr}`}
                      style={{
                        position: 'absolute', top: '8px', bottom: '8px',
                        left, width,
                        background: colorFor(idx), borderRadius: '4px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontSize: '10px', fontWeight: '700', overflow: 'hidden',
                        padding: '0 4px',
                      }}>
                      {lenH >= 1 ? `${startStr}~${endStr}` : ''}
                    </div>
                  );
                })}

                {ds.shift?.isHoliday && (
                  <div style={{ position: 'absolute', top: '4px', right: '6px',
                                fontSize: '10px', color: '#52525B', fontWeight: '700' }}>
                    🏖️ 휴일
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// 주별: 7일 × 직원 카드 (work + break 모두 표시)
// ─────────────────────────────────────────────────────────
function WeekView({ refDate, employees, schedules }) {
  const start = new Date(refDate);
  const dow = start.getDay();
  const offset = dow === 0 ? -6 : 1 - dow;
  start.setDate(start.getDate() + offset);

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });

  const dayData = weekDays.map(d => {
    const monthKey = `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
    const dd = schedules?.[monthKey]?.[d.getDate()] || {};
    return employees.map(emp => ({
      emp,
      shift: dd[emp.id] || null,
      blocks: getBlocks(dd[emp.id]),
      netMin: shiftNetMin(dd[emp.id]),
    }));
  });

  const weekTotalMin = dayData.flat().reduce((s, x) => s + x.netMin, 0);

  return (
    <div>
      <SummaryBar items={[{ label: '주간 총 근무', value: `${(weekTotalMin / 60).toFixed(1)}h` }]} />

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px',
                    fontSize: '11px', color: '#6b7280' }}>
        {employees.map((emp, idx) => (
          <span key={emp.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: colorFor(idx) }} />
            {emp.name}
          </span>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px',
                    background: '#fff', borderRadius: '10px', padding: '8px',
                    border: '1px solid #e5e7eb' }}>
        {weekDays.map((d, i) => {
          const dayEmps = dayData[i].filter(x => x.netMin > 0);
          const isWeekend = d.getDay() === 0 || d.getDay() === 6;
          const isToday = d.toDateString() === new Date().toDateString();
          return (
            <div key={i} style={{
              padding: '8px', minHeight: '140px', borderRadius: '6px',
              background: isToday ? '#fef9c3' : isWeekend ? '#fafaf9' : '#fff',
              border: '1px solid #f3f4f6',
            }}>
              <div style={{ fontSize: '11px', fontWeight: '700', marginBottom: '6px',
                            color: d.getDay() === 0 ? '#52525B' : d.getDay() === 6 ? '#2563eb' : '#374151' }}>
                {pad(d.getMonth() + 1)}/{pad(d.getDate())} ({KOR_DAYS[d.getDay()]})
              </div>
              {dayEmps.length === 0 ? (
                <div style={{ fontSize: '10px', color: '#d1d5db', textAlign: 'center', padding: '20px 0' }}>—</div>
              ) : (
                dayEmps.map(de => {
                  const idx = employees.findIndex(e => e.id === de.emp.id);
                  return (
                    <div key={de.emp.id} style={{
                      fontSize: '10px', padding: '4px 6px', marginBottom: '4px',
                      borderRadius: '4px', background: colorFor(idx), color: '#fff',
                    }}>
                      <div style={{ fontWeight: '700' }}>{de.emp.name}</div>
                      {de.blocks.works.map((b, k) => (
                        <div key={k} style={{ fontSize: '9px', opacity: 0.95 }}>
                          {b.start}~{b.end}
                        </div>
                      ))}
                      {de.blocks.breaks.map((b, k) => (
                        <div key={`br-${k}`} style={{ fontSize: '9px', opacity: 0.85,
                                                       background: 'rgba(255,255,255,0.2)',
                                                       borderRadius: '2px', padding: '0 3px',
                                                       marginTop: '2px' }}>
                          ☕ {b.start}~{b.end}
                        </div>
                      ))}
                    </div>
                  );
                })
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// 월별: 캘린더 + 직원별 근무시간 직접 표시
// ─────────────────────────────────────────────────────────
function MonthView({ refDate, employees, schedules }) {
  const y = refDate.getFullYear(), m = refDate.getMonth() + 1;
  const monthKey = `${y}-${pad(m)}`;
  const monthData = schedules?.[monthKey] || {};

  const firstDow = new Date(y, m - 1, 1).getDay();
  const days = new Date(y, m, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  let totalMin = 0;
  for (let d = 1; d <= days; d++) {
    for (const emp of employees) {
      totalMin += shiftNetMin(monthData?.[d]?.[emp.id]);
    }
  }

  return (
    <div>
      <SummaryBar items={[{ label: '월간 총 근무', value: `${(totalMin / 60).toFixed(1)}h` }]} />

      <div style={{ background: '#fff', borderRadius: '10px', padding: '8px',
                    border: '1px solid #e5e7eb' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
                      marginBottom: '4px', fontSize: '11px', fontWeight: '700' }}>
          {KOR_DAYS.map((d, i) => (
            <div key={d} style={{
              textAlign: 'center', padding: '6px',
              color: i === 0 ? '#52525B' : i === 6 ? '#2563eb' : '#374151',
            }}>{d}</div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
          {cells.map((day, i) => {
            if (day === null) return <div key={i} />;
            const dd = monthData?.[day] || {};
            // 그 날 일하는 직원들
            const empsWorking = employees
              .map((emp, idx) => ({ emp, idx, blocks: getBlocks(dd[emp.id]), netMin: shiftNetMin(dd[emp.id]) }))
              .filter(x => x.netMin > 0);
            const dow = (firstDow + day - 1) % 7;
            const isToday = new Date().toDateString() === new Date(y, m - 1, day).toDateString();

            return (
              <div key={i} style={{
                minHeight: '90px', padding: '5px', borderRadius: '6px',
                background: isToday ? '#fef9c3' : '#fafaf9',
                border: '1px solid #f3f4f6',
                overflow: 'hidden',
              }}>
                <div style={{ fontSize: '11px', fontWeight: '700', marginBottom: '3px',
                              color: dow === 0 ? '#52525B' : dow === 6 ? '#2563eb' : '#374151' }}>
                  {day}
                </div>
                {empsWorking.slice(0, 4).map(({ emp, idx, blocks }) => {
                  const works = blocks.works;
                  const firstWork = works[0];
                  const lastWork = works[works.length - 1];
                  const timeStr = firstWork && lastWork ? `${firstWork.start}~${lastWork.end}` : '';
                  return (
                    <div key={emp.id} style={{
                      fontSize: '9px', marginBottom: '2px', padding: '1px 3px',
                      borderRadius: '2px', background: colorFor(idx), color: '#fff',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }} title={`${emp.name} ${timeStr}`}>
                      <strong>{emp.name.slice(0, 2)}</strong> {timeStr}
                    </div>
                  );
                })}
                {empsWorking.length > 4 && (
                  <div style={{ fontSize: '9px', color: '#9ca3af', marginTop: '2px' }}>
                    +{empsWorking.length - 4}명
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SummaryBar({ items }) {
  return (
    <div style={{ background: '#FF6F0F', color: '#fff', borderRadius: '8px',
                  padding: '10px 14px', marginBottom: '12px', display: 'grid',
                  gridTemplateColumns: `repeat(${items.length}, 1fr)`, gap: '8px', textAlign: 'center' }}>
      {items.map((it, i) => (
        <div key={i}>
          <div style={{ fontSize: '11px', opacity: 0.85 }}>{it.label}</div>
          <div style={{ fontSize: '16px', fontWeight: '700', marginTop: '4px' }}>{it.value}</div>
        </div>
      ))}
    </div>
  );
}
